import { useMemo } from "react";
import { useQuery } from "@tanstack/react-query";
import dayjs from "dayjs";
import { getNotificationsApi } from "api/apiCall";
import { IN_PROGRESS, IN_STATUS_CHECK, INVOICES } from "global/paths";

const DATE_FORMAT = "YYYY-MM-DD";
const QUERY_DATE_FORMAT = "DD-MM-YYYY";
const IN_PROGRESS_NOTIFICATION_TYPES = new Set([
  "bulk_add_several_employees_failed",
  "bulk_update_several_employees_failed",
]);
const IN_STATUS_CHECK_NOTIFICATION_TYPES = new Set(["bulk_change_multiple_employee_status_failed"]);
const INVOICE_DUE_NOTIFICATION_TYPES = new Set(["invoice_due_t_minus_3", "invoice_due_t_minus_1", "invoice_due_t_0"]);
const INVOICE_OVERDUE_NOTIFICATION_TYPES = new Set(["invoice_overdue"]);

const formatDate = (value) => {
  if (!value) return null;
  const parsed = dayjs(value);
  return parsed.isValid() ? parsed.format(QUERY_DATE_FORMAT) : null;
};

const resolveRedirectTarget = (itemType, redirectTargetRaw) => {
  if (redirectTargetRaw) return redirectTargetRaw;
  if (IN_PROGRESS_NOTIFICATION_TYPES.has(itemType)) return IN_PROGRESS;
  if (IN_STATUS_CHECK_NOTIFICATION_TYPES.has(itemType)) return IN_STATUS_CHECK;
  if (INVOICE_DUE_NOTIFICATION_TYPES.has(itemType) || INVOICE_OVERDUE_NOTIFICATION_TYPES.has(itemType)) {
    return INVOICES;
  }
  return undefined;
};

const normalizeIsRead = (value) => {
  if (value === undefined || value === null) return false;
  if (typeof value === "boolean") return value;
  if (typeof value === "number") return value === 1;
  if (typeof value === "string") {
    const normalized = value.trim().toLowerCase();
    return normalized === "true" || normalized === "1";
  }
  return Boolean(value);
};

const normalizeStatus = (statusValue, isRead) => {
  if (typeof statusValue === "string") {
    const normalized = statusValue.trim().toLowerCase();
    if (normalized === "read" || normalized === "unread") return normalized;
  }
  return isRead ? "read" : "unread";
};

const normalizeNotification = (item) => {
  const { createdAt } = item;
  const createdAtDate = createdAt ? dayjs(createdAt) : null;
  const createdAtValid = createdAtDate && createdAtDate.isValid();
  const type = item.notificationType;
  const redirectTargetRaw = item.redirectTarget;
  const redirectTarget = resolveRedirectTarget(type, redirectTargetRaw);
  const redirectParams = item.redirectParams || {};
  const isRead = normalizeIsRead(item.isRead);
  const status = normalizeStatus(item.status, isRead);

  return {
    id: item.id,
    type,
    createdAt,
    date: createdAtValid ? createdAtDate.format(DATE_FORMAT) : null,
    time: createdAtValid ? createdAtDate.format("HH:mm") : item.time || "00:00",
    status,
    meta: { ...(item.meta || {}), ...redirectParams },
    title_en: item.title_en,
    body_en: item.body_en,
    title_id: item.title_id,
    body_id: item.body_id,
    isActionable: item.isActionable,
    redirectTarget,
    redirectParams,
    highlight: item.highlight,
    isRead,
  };
};

const toStatusParam = (status) => {
  if (!status) return null;
  if (typeof status === "string") return status.toUpperCase();
  return status;
};

const DEFAULT_POLL_INTERVAL_MS = 30000;

export function useNotifications({
  status = "all",
  limit,
  skip,
  page,
  startDate,
  endDate,
  enabled = true,
  pollIntervalMs = DEFAULT_POLL_INTERVAL_MS,
} = {}) {
  const queryParams = useMemo(() => {
    const rawParams = {
      status: toStatusParam(status),
      limit,
      skip,
      page,
      fromDate: formatDate(startDate),
      toDate: formatDate(endDate),
    };
    return Object.fromEntries(
      Object.entries(rawParams).filter(([, value]) => value !== null && value !== undefined && value !== ""),
    );
  }, [status, limit, skip, page, startDate, endDate]);

  const query = useQuery(
    {
      queryKey: ["notifications", queryParams],
      queryFn: () => getNotificationsApi(queryParams),
      refetchOnWindowFocus: false,
      refetchInterval: pollIntervalMs,
      refetchIntervalInBackground: true,
      keepPreviousData: true,
      enabled,
    },
    {
      enabled,
      refetchOnMount: false,
      refetchOnReconnect: false,
      refetchOnWindowFocus: false,
    },
  );

  const rawData = Array.isArray(query.data?.data) ? query.data.data : [];
  const normalized = useMemo(() => rawData.map(normalizeNotification), [rawData]);

  const totalCount = Number.isFinite(query.data?.count) ? query.data.count : rawData.length;

  const hasUnreadNotifications = Boolean(
    query.data?.hasUnreadNotifications ??
      query.data?.has_unread_notifications ??
      query.data?.hasUnreadNotifcations ??
      query.data?.data?.hasUnreadNotifications ??
      query.data?.data?.has_unread_notifications ??
      query.data?.data?.hasUnreadNotifcations,
  );

  return {
    ...query,
    notifications: normalized,
    totalCount,
    hasUnreadNotifications,
  };
}
